import { create } from 'zustand'
import { api, describeError, setUnauthenticatedHandler } from '@/services/api'
import { env } from '@/lib/env'
import { clearAuthToken, setAuthToken } from '@/lib/authToken'
import type { PersonRole } from '@/types'

export interface SignInConfig {
  turnstileEnabled: boolean
  turnstileSiteKey: string
  /** Whether to offer "Sign in with MyMDV". */
  ssoEnabled?: boolean
  ssoLabel?: string
}

export interface AuthUser {
  id: string
  firstName: string
  lastName: string
  email: string
  /* `PersonRole`, not a copy of it. This was its own inline union, so adding
     `viewer` to the real type left this one behind and TypeScript narrowed
     `role` to 'member' in usePermissions — the compiler caught a divergence
     that would otherwise have been a signed-in viewer treated as a member. */
  role: PersonRole
  avatarColor: string
  avatarUrl?: string | null
  mustChangePassword: boolean
  notifyEmail: boolean
  lastLoginAt?: string | null
}

interface AuthState {
  /** `unknown` until the session has been checked — guards must not flash. */
  status: 'unknown' | 'authenticated' | 'anonymous' | 'setup'
  user: AuthUser | null
  error: string | null
  /** A server-side blocker (e.g. a migration that has not been run yet). */
  serverMessage: string | null
  /** Public sign-in configuration, served rather than baked into the build. */
  signInConfig: SignInConfig | null
  busy: boolean

  check: () => Promise<void>
  loadSignInConfig: () => Promise<void>
  login: (email: string, password: string, turnstileToken?: string | null) => Promise<boolean>
  setup: (input: {
    email: string
    password: string
    firstName: string
    lastName: string
    turnstileToken?: string | null
  }) => Promise<boolean>
  logout: () => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>
  setNotifyEmail: (enabled: boolean) => Promise<void>
  setAvatarUrl: (avatarUrl: string | null) => void
  completeSso: (code: string) => Promise<boolean>
  /** Picks up an existing MyMDV session without showing anybody a page. */
  attemptSilentSso: () => Promise<boolean>
  clearError: () => void
}

interface MeResponse {
  authenticated: boolean
  needsSetup: boolean
  user: AuthUser | null
}

/**
 * In mock mode there is no server to authenticate against, so the app runs
 * open — the same way it did before accounts existed. Every real deployment
 * (VITE_USE_MOCK_API=false) is gated.
 */
const MOCK_USER: AuthUser = {
  id: 'person_ian',
  firstName: 'Local',
  lastName: 'Preview',
  email: 'preview@localhost',
  role: 'admin',
  avatarColor: '#15803d',
  mustChangePassword: false,
  notifyEmail: false,
}

/** Guards against a burst of 401s each firing its own verification. */
let verifying = false

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'unknown',
  user: null,
  error: null,
  serverMessage: null,
  signInConfig: null,
  busy: false,

  async check() {
    /*
     * A 401 usually means the session really has gone — but not always, and
     * throwing someone back to sign-in the instant one call fails produced a
     * login that appeared to work and then bounced. Ask the server first, and
     * only tear down if it agrees we are anonymous.
     */
    setUnauthenticatedHandler(() => {
      if (env.useMockApi || get().status !== 'authenticated' || verifying) return
      verifying = true
      void api
        .get<MeResponse>('/auth/me')
        .then((me) => {
          if (!me.authenticated) {
            clearAuthToken()
            set({ status: 'anonymous', user: null })
          }
        })
        .catch(() => {
          // Could not ask — leave the session alone rather than guess.
        })
        .finally(() => {
          verifying = false
        })
    })

    if (env.useMockApi) {
      set({ status: 'authenticated', user: MOCK_USER })
      return
    }
    try {
      const me = await api.get<MeResponse>('/auth/me')
      if (me.authenticated && me.user) set({ status: 'authenticated', user: me.user, error: null })
      else set({ status: me.needsSetup ? 'setup' : 'anonymous', user: null })
    } catch (checkError) {
      // Usually just "not signed in" — but a server that cannot answer at all
      // needs to say why, or the sign-in screen is a dead end.
      const message = describeError(checkError)
      set({
        status: 'anonymous',
        user: null,
        serverMessage: /migration|database|not configured/i.test(message) ? message : null,
      })
    }
  },

  async loadSignInConfig() {
    if (env.useMockApi) {
      set({ signInConfig: { turnstileEnabled: false, turnstileSiteKey: '', ssoEnabled: false } })
      return
    }
    try {
      set({
        signInConfig: await api.get<SignInConfig>(
          '/auth/config',
        ),
      })
    } catch {
      // An older server has no such endpoint — carry on without the widget
      // rather than blocking sign-in entirely.
      set({ signInConfig: { turnstileEnabled: false, turnstileSiteKey: '', ssoEnabled: false } })
    }
  },

  async login(email, password, turnstileToken) {
    set({ busy: true, error: null })
    try {
      const result = await api.post<{ user: AuthUser; token?: string }>('/auth/login', {
        email,
        password,
        turnstileToken,
      })
      // Stored before any further call, so the very next request is
      // authenticated even if the session cookie did not stick.
      if (result.token) setAuthToken(result.token)
      set({ status: 'authenticated', user: result.user })
      return true
    } catch (loginError) {
      set({ error: describeError(loginError) })
      return false
    } finally {
      set({ busy: false })
    }
  },

  async setup(input) {
    set({ busy: true, error: null })
    try {
      const result = await api.post<{ user: AuthUser; token?: string }>('/auth/setup', input)
      if (result.token) setAuthToken(result.token)
      set({ status: 'authenticated', user: result.user })
      return true
    } catch (setupError) {
      set({ error: describeError(setupError) })
      return false
    } finally {
      set({ busy: false })
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout')
    } catch {
      // Even if the call fails, drop the local session.
    }
    clearAuthToken()
    set({ status: 'anonymous', user: null })
  },

  async changePassword(currentPassword, newPassword) {
    set({ busy: true, error: null })
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword })
      const user = get().user
      if (user) set({ user: { ...user, mustChangePassword: false } })
      return true
    } catch (changeError) {
      set({ error: describeError(changeError) })
      return false
    } finally {
      set({ busy: false })
    }
  },

  async setNotifyEmail(enabled) {
    const user = get().user
    if (!user) return
    set({ user: { ...user, notifyEmail: enabled } })
    try {
      await api.patch('/auth/preferences', { notifyEmail: enabled })
    } catch (prefError) {
      set({ user: { ...user, notifyEmail: !enabled }, error: describeError(prefError) })
    }
  },

  setAvatarUrl(avatarUrl) {
    const user = get().user
    if (user) set({ user: { ...user, avatarUrl } })
  },

  /**
   * Finishes a "Sign in with MyMDV" round trip.
   *
   * The provider hands the browser back with a single-use code in the URL,
   * never a real token — a token in a redirect ends up in browser history and
   * the server access log. This trades it for the real thing over POST.
   */
  async completeSso(code) {
    set({ busy: true, error: null })
    try {
      const result = await api.post<{ user: AuthUser; token?: string }>('/auth/oidc/exchange', { code })
      if (result.token) setAuthToken(result.token)
      set({ status: 'authenticated', user: result.user })
      return true
    } catch (ssoError) {
      set({ error: describeError(ssoError) })
      return false
    } finally {
      set({ busy: false })
    }
  },

  /**
   * Signs somebody in because they are already signed in to MyMDV Hub.
   *
   * A hidden frame asks Hub with `prompt=none`, which it answers with a
   * redirect either way and never a page. The frame lands on a blank
   * same-origin result page, so its query string can be read directly — no
   * postMessage, and nothing inline for the CSP to block.
   *
   * Every failure is silent on purpose. Somebody with no Hub session is not
   * having a problem, they are simply signed out, and the sign-in form behind
   * this is the correct thing for them to see.
   */
  async attemptSilentSso() {
    if (env.useMockApi) return false
    if (!get().signInConfig?.ssoEnabled) return false

    const code = await new Promise<string | null>((resolve) => {
      const frame = document.createElement('iframe')
      frame.setAttribute('aria-hidden', 'true')
      frame.setAttribute('tabindex', '-1')
      frame.setAttribute('title', 'MyMDV sign-in check')
      frame.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden'

      let settled = false
      const finish = (value: string | null) => {
        if (settled) return
        settled = true
        window.clearTimeout(timer)
        frame.remove()
        resolve(value)
      }

      // A provider that never answers must not leave the door hidden forever.
      const timer = window.setTimeout(() => finish(null), 8000)

      frame.addEventListener('load', () => {
        try {
          const params = new URLSearchParams(frame.contentWindow?.location.search ?? '')
          if (params.has('sso')) finish(params.get('sso'))
          else if (params.has('error')) finish(null)
        } catch {
          // Mid-flight on Hub's origin, which this cannot read. That is the
          // expected state between redirects — wait for the next load.
        }
      })

      frame.src = `${env.apiBaseUrl}/auth/oidc/start?silent=1`
      document.body.appendChild(frame)
    })

    if (!code) return false
    return get().completeSso(code)
  },

  clearError() {
    set({ error: null })
  },
}))
